import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

const JoinTeam = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [isLoading, setIsLoading] = useState(true);
    const [isValid, setIsValid] = useState(false);
    const [userData, setUserData] = useState(null);
    const [error, setError] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    useEffect(() => {
        if (!token) {
            navigate('/');
            return;
        }

        const verifyToken = async () => {
            try {
                const response = await api.get(`/api/auth/verify-invite/${token}`);
                const contentType = response.headers.get("content-type");

                if (response.ok) {
                    if (contentType && contentType.includes("application/json")) {
                        const data = await response.json();
                        setUserData(data);
                        setIsValid(true);
                    } else {
                        setError('Invalid or expired invite link.');
                        setIsValid(false);
                    }
                } else {
                    let errorMessage = 'Invalid or expired invite link.';
                    if (contentType && contentType.includes("application/json")) {
                        try {
                            const data = await response.json();
                            errorMessage = data.message || errorMessage;
                        } catch (e) {
                            console.error("Failed to parse error JSON", e);
                        }
                    } else {
                        // Handle plain text or other formats
                        const text = await response.text();
                        errorMessage = text || `Error ${response.status}: ${response.statusText}`;
                    }
                    setError(errorMessage);
                }
            } catch (err) {
                console.error("Verification network/internal error:", err);
                setError('An error occurred verifying your invite. Please check your connection or try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        verifyToken();
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        try {
            const response = await api.post('/api/auth/complete-invite', {
                token,
                password
            });

            if (response.ok) {
                const data = await response.json();
                // Save token to localStorage if that's how auth is handled (Login.js didn't show it but implied)
                // Login.js: const data = await response.json(); console.log("Login successful", data);
                // We should probably save the token.
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));

                alert("Account activated! Welcome to the team.");
                navigate('/artisan/dashboard');
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to activate account.');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <div className="auth-container">
                <div className="auth-box">
                    <p>Verifying your invite...</p>
                </div>
            </div>
        );
    }

    if (!isValid) {
        return (
            <div className="auth-container">
                <div className="auth-box">
                    <h2>Invite Error</h2>
                    <p className="error-message">{error}</p>
                    <button onClick={() => navigate('/')} className="btn-secondary full-width">Go Home</button>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="auth-box fade-in-section in-view">
                <h2>Welcome, {userData.name}!</h2>
                <p className="auth-subtitle">Set your password to join the team.</p>

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Email Address</label>
                        <input
                            type="email"
                            value={userData.email}
                            disabled
                        />
                    </div>

                    <div className="form-group">
                        <label>Create Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="Min. 8 characters"
                            minLength={8}
                        />
                    </div>

                    <div className="form-group">
                        <label>Confirm Password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            placeholder="Re-enter password"
                        />
                    </div>

                    <button type="submit" className="btn-primary full-width">Activate Account</button>
                </form>
            </div>
        </div>
    );
};

export default JoinTeam;
